import { ZippedFiles } from "../readGerberZip";
import { EMPTY_GERBER_FILE_MAPPING } from "../../index";
import _ from "lodash";
import { getFileColumns } from "../../../../../utils/parseFile";
import {
  Board,
  BOMFileInfo,
  PNPFileInfo,
  UploadConfig,
} from "../../../../../reducers/uploadedBoard";

async function getPreviousBOMInfoForPreviousUpload(
  zippedFiles: ZippedFiles,
  uploadConfig: UploadConfig,
): Promise<{
  bomFileInfo: BOMFileInfo | null;
  pnpFileInfo: PNPFileInfo | null;
}> {
  const matchingBOMFile = zippedFiles.bomOrPNPFiles.find(
    ({ path }) => uploadConfig.bom_file_info?.file_name == path,
  );
  if (uploadConfig.bom_file_info !== null && matchingBOMFile !== undefined) {
    const bomColumnInfo = await getFileColumns(
      matchingBOMFile.path,
      matchingBOMFile.content as Blob,
    );
    const bomColumns = bomColumnInfo?.headers || [];
    const bomFileInfo = {
      file_name: matchingBOMFile.path,
      header_row_index: bomColumnInfo?.headerRowIndex,
      designator_column: bomColumns.find(
        (column) => column == uploadConfig.bom_file_info?.designator_column,
      ),
      mpn_column: bomColumns.find(
        (column) => column == uploadConfig.bom_file_info?.mpn_column,
      ),
      manufacturer_column: bomColumns.find(
        (column) => column == uploadConfig.bom_file_info?.manufacturer_column,
      ),
      footprint_column: bomColumns.find(
        (column) => column == uploadConfig.bom_file_info?.footprint_column,
      ),
      value_text_column: bomColumns.find(
        (column) => column == uploadConfig.bom_file_info?.value_text_column,
      ),
    };
    const matchingPNPFile = zippedFiles.bomOrPNPFiles.find(
      ({ path }) => uploadConfig.pnp_file_info?.file_name == path,
    );
    if (uploadConfig.pnp_file_info !== null && matchingPNPFile !== undefined) {
      const pnpColumnInfo = await getFileColumns(
        matchingPNPFile.path,
        matchingPNPFile.content as Blob,
      );
      const pnpColumns = pnpColumnInfo?.headers || [];
      return {
        bomFileInfo,
        pnpFileInfo: {
          file_name: matchingPNPFile.path,
          header_row_index: pnpColumnInfo?.headerRowIndex,
          designator_column: pnpColumns.find(
            (column) => column == uploadConfig.pnp_file_info?.designator_column,
          ),
          x_column: pnpColumns.find(
            (column) => column == uploadConfig.pnp_file_info?.x_column,
          ),
          y_column: pnpColumns.find(
            (column) => column == uploadConfig.pnp_file_info?.y_column,
          ),
          rotation_column: pnpColumns.find(
            (column) => column == uploadConfig.pnp_file_info?.rotation_column,
          ),
          mount_type_column: pnpColumns.find(
            (column) => column == uploadConfig.pnp_file_info?.mount_type_column,
          ),
          unit: uploadConfig.pnp_file_info.unit,
        },
      };
    }
    return {
      bomFileInfo,
      pnpFileInfo: null,
    };
  }
  return {
    bomFileInfo: null,
    pnpFileInfo: null,
  };
}

async function getPreviousAdditionalFilesPreviousUpload(
  zippedFiles: ZippedFiles,
  uploadConfig: UploadConfig,
): Promise<{
  assemblyDrawing: string[] | null;
  fabDrawing: string[] | null;
  referenceFile: string[] | null;
}> {
  const maybeAssemblyDrawings: string[] = [];
  const maybeFabDrawings: string[] = [];
  const additionalFiles: string[] = [];

  zippedFiles.additionalFiles.forEach(({ path }) => {
    if (uploadConfig.assembly_drawing?.some((file_name) => file_name == path)) {
      maybeAssemblyDrawings.push(path);
    } else if (
      uploadConfig.fab_drawing?.some((file_name) => file_name == path)
    ) {
      maybeFabDrawings.push(path);
    } else if (
      uploadConfig.reference_file?.some((file_name) => file_name == path)
    ) {
      additionalFiles.push(path);
    }
  });

  return {
    assemblyDrawing: maybeAssemblyDrawings || null,
    fabDrawing: maybeFabDrawings || null,
    referenceFile: additionalFiles || null,
  };
}

export async function getPreviousUploadSuggestions(
  zippedFiles: ZippedFiles,
  selectedBoard: Board | null,
): Promise<UploadConfig> {
  const gerberFileMapping = _.cloneDeep(EMPTY_GERBER_FILE_MAPPING);
  if (selectedBoard === null || selectedBoard.upload_config === null) {
    return {
      gerber_file_layer_mapping: gerberFileMapping,
      bom_file_info: null,
      pnp_file_info: null,
      assembly_drawing: null,
      fab_drawing: null,
      reference_file: null,
    };
  }
  if (selectedBoard.upload_config.gerber_file_layer_mapping !== null) {
    Object.entries(
      selectedBoard.upload_config.gerber_file_layer_mapping,
    ).forEach(([layerName, fileOrFiles]) => {
      if (typeof fileOrFiles === "string") {
        if (zippedFiles.allFiles.some(({ path }) => path == fileOrFiles)) {
          gerberFileMapping[layerName] = fileOrFiles;
        }
      } else {
        gerberFileMapping[layerName] = fileOrFiles.filter((file) =>
          zippedFiles.allFiles.some(({ path }) => path == file),
        );
      }
    });
  }
  const { bomFileInfo, pnpFileInfo } =
    await getPreviousBOMInfoForPreviousUpload(
      zippedFiles,
      selectedBoard.upload_config,
    );
  const { assemblyDrawing, fabDrawing, referenceFile } =
    await getPreviousAdditionalFilesPreviousUpload(
      zippedFiles,
      selectedBoard.upload_config,
    );
  return {
    gerber_file_layer_mapping: gerberFileMapping,
    bom_file_info: bomFileInfo,
    pnp_file_info: pnpFileInfo,
    assembly_drawing: assemblyDrawing,
    fab_drawing: fabDrawing,
    reference_file: referenceFile,
  };
}
